import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import DataTable from '../../../components/orizon-ui/DataTable';
import SearchInput from '../../../components/orizon-ui/SearchInput';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import ExportMenu from '../../../components/orizon-ui/ExportMenu';
import ImpostazioniTabella, { FiltriBadge } from '../../../components/orizon-ui/ImpostazioniTabella';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useListaColata } from '../../../components/colata/useListaColata';
import { configScorta, formattaQta } from '../../../components/gru/scorta';
import { esportaCSV, esportaXLSX, esportaPDF } from '../../../components/gru/esporta';
import { FORMATI_SCANNER } from '../../../lib/colataBarcode';
import { useColataPermessi } from '../../../components/colata/ColataPermessi';
import {
  colataOpzioni, colataLista, colataArticolo, colataEsporta, mapColataError,
} from '../../../lib/services/colataService';
import FormArticolo from './FormArticolo';
import DettaglioArticolo from './DettaglioArticolo';
import AuditQta from './AuditQta';
import CompatibilitaModal from './CompatibilitaModal';
import CartellinoModal from './CartellinoModal';
import { CaricoModal } from '../../../components/colata/AzioniGiacenza';
import { opzioniFiltroImpianti } from '../../../components/colata/opzioniZona';

const MAX_FILTRI = 5;

const STATI_SCORTA = [
  { value: 'esaurito', label: 'Esaurito' },
  { value: 'sotto_scorta', label: 'Sotto scorta' },
  { value: 'ok', label: 'Disponibile' },
];

// Colonne di cui si può cambiare la visibilità (l'ultima, "Apri", è sempre presente)
const COLONNE_VISIBILI = [
  { key: 'codice', label: 'Codice' },
  { key: 'nome', label: 'Articolo' },
  { key: 'reparto_label', label: 'Ricambio' },
  { key: 'zone_codici', label: 'Zona' },
  { key: 'impianti_nomi', label: 'Impianto' },
  { key: 'qta', label: 'Q.tà' },
  { key: 'qta_min', label: 'Min' },
  { key: 'stato_scorta', label: 'Stato' },
  { key: 'ubicazione_codice', label: 'Ubicazione' },
  { key: 'n_ordini_aperti', label: 'In ordine' },
];

const TabellaArticoli = () => {
  const toast = useToast();
  const { scrivere } = useColataPermessi(); // false = "Sola lettura"

  // elenchi per i filtri (da tabelle)
  const [reparti, setReparti] = useState([]);
  const [elencoZone, setElencoZone] = useState([]);
  const [elencoImpianti, setElencoImpianti] = useState([]);
  const [magazzini, setMagazzini] = useState([]);
  const [ubicazioni, setUbicazioni] = useState([]);
  const [unita, setUnita] = useState([]);

  // impostazioni tabella
  const [impostazioniAperte, setImpostazioniAperte] = useState(false);
  const [colVisibili, setColVisibili] = useState({});
  const [densita, setDensita] = useState('normale');
  const [filtriUi, setFiltriUi] = useState([]); // [{ col, val }]
  const [esportando, setEsportando] = useState(false);

  // modal
  const [apertoId, setApertoId] = useState(null);
  const [compatibilitaArticolo, setCompatibilitaArticolo] = useState(null); // riga articolo del modale Compatibilità
  const [cartellinoArticolo, setCartellinoArticolo] = useState(null); // riga articolo del modale Cartellino
  const [formAperto, setFormAperto] = useState(false);
  const [caricoIniziale, setCaricoIniziale] = useState(null);
  const [auditAperto, setAuditAperto] = useState(false);
  const [auditN, setAuditN] = useState(0);

  // Definizione dei filtri disponibili: per ciascuno come si traduce in condizione sul database
  const definizioniFiltri = useMemo(() => {
    const nomeMagazzino = (id) => magazzini.find((m) => m.id === id)?.label ?? '';
    return [
      {
        key: 'reparto', label: 'Ricambio', tipo: 'select',
        opzioni: reparti.map((r) => ({ value: r.id, label: r.label })),
        db: (v) => ['reparto_id', v],
      },
      {
        key: 'zona', label: 'Zona', tipo: 'select',
        opzioni: elencoZone.map((g) => ({ value: g.id, label: `${g.codice} — ${g.nome}` })),
        db: (v) => ['zone_ids', { contiene: v }],
      },
      {
        key: 'impianto', label: 'Impianto', tipo: 'select',
        opzioni: opzioniFiltroImpianti(elencoImpianti, elencoZone),
        db: (v) => ['impianti_ids', { contiene: v }],
      },
      {
        key: 'magazzino', label: 'Magazzino', tipo: 'select',
        opzioni: magazzini.map((m) => ({ value: m.id, label: m.label })),
        db: (v) => ['magazzino_id', v],
      },
      {
        key: 'ubicazione', label: 'Ubicazione', tipo: 'select',
        opzioni: ubicazioni.map((u) => ({ value: u.id, label: `${nomeMagazzino(u.magazzino_id)} · ${u.codice}` })),
        db: (v) => ['ubicazione_id', v],
      },
      {
        key: 'unita', label: 'Unità di misura', tipo: 'select',
        opzioni: unita.map((u) => ({ value: u.id, label: u.label })),
        db: (v) => ['unita_id', v],
      },
      { key: 'stato', label: 'Stato scorta', tipo: 'select', opzioni: STATI_SCORTA, db: (v) => ['stato_scorta', v] },
      { key: 'nome', label: 'Articolo', tipo: 'testo', db: (v) => ['nome', { ilike: v }] },
      // almeno un componente associato all'articolo ha un'etichetta che contiene il testo (filtro lato server)
      { key: 'etichetta', label: 'Etichetta', tipo: 'testo', db: (v) => ['etichette_componenti', { ilike: v }] },
      { key: 'codice', label: 'Codice', tipo: 'testo', db: (v) => ['codice', { ilike: v }] },
      { key: 'sap', label: 'Codice SAP', tipo: 'testo', db: (v) => ['codice_sap', { ilike: v }] },
      { key: 'fornitore', label: 'Codice fornitore', tipo: 'testo', db: (v) => ['codice_fornitore', { ilike: v }] },
    ];
  }, [reparti, elencoZone, elencoImpianti, magazzini, ubicazioni, unita]);

  const filtriAttivi = useMemo(
    () => filtriUi.filter((f) => f.col && f.val),
    [filtriUi],
  );

  // condizioni sul database derivate dai filtri attivi
  const filtri = useMemo(() => {
    const risultato = {};
    filtriAttivi.forEach((f) => {
      const def = definizioniFiltri.find((d) => d.key === f.col);
      if (!def) return;
      const [colonna, valore] = def.db(f.val);
      risultato[colonna] = valore;
    });
    return risultato;
  }, [filtriAttivi, definizioniFiltri]);

  const lista = useListaColata({
    entita: 'articoli',
    filtri,
    ordineIniziale: { key: 'nome', direction: 'asc' },
    perPagina: 25,
  });

  const aggiornaAudit = useCallback(() => {
    colataLista('audit', { perPagina: 1 })
      .then((r) => setAuditN(r.totale))
      .catch(() => setAuditN(0));
  }, []);

  useEffect(() => {
    let annullato = false;
    Promise.all([
      colataOpzioni('reparti'), colataOpzioni('zone'), colataOpzioni('magazzini'), colataOpzioni('ubicazioni'), colataOpzioni('unita'),
      colataOpzioni('impianti'),
    ])
      .then(([r, g, m, u, un, imp]) => {
        if (annullato) return;
        setReparti(r);
        setElencoZone(g);
        setElencoImpianti(imp);
        setMagazzini(m);
        setUbicazioni(u);
        setUnita(un);
      })
      .catch((e) => {
        if (!annullato) toast.error(mapColataError(e).message);
      });
    aggiornaAudit();
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dopoModifica = useCallback(() => {
    lista.ricarica();
    aggiornaAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lista.ricarica, aggiornaAudit]);

  // dopo la creazione: proponi il carico iniziale
  const articoloCreato = async (esito) => {
    setFormAperto(false);
    dopoModifica();
    try {
      const articolo = await colataArticolo(esito.id);
      if (articolo) setCaricoIniziale(articolo);
    } catch (e) {
      toast.error(mapColataError(e).message);
    }
  };

  // ─── Esportazione ──────────────────────────────────────────────────────────
  const COLONNE_EXPORT = [
    { label: 'Codice', valore: (r) => r.codice, larghezza: 16, align: 'left' },
    { label: 'Articolo', valore: (r) => r.nome, larghezza: 42, align: 'left' },
    { label: 'Ricambio', valore: (r) => r.reparto_label, larghezza: 14, align: 'center' },
    { label: 'Zona', valore: (r) => r.zone_codici ?? '', larghezza: 18, align: 'center' },
    { label: 'Impianto', valore: (r) => r.impianti_nomi ?? '', larghezza: 26, align: 'left' },
    {
      label: 'Q.tà', valore: (r) => Number(r.qta), larghezza: 10, align: 'center',
      colore: (r) => {
        if (r.stato_scorta === 'esaurito') return [239, 68, 68];
        if (r.stato_scorta === 'sotto_scorta') return [245, 158, 11];
        return [16, 185, 129];
      },
    },
    { label: 'Unità', valore: (r) => r.unita_label, larghezza: 8, align: 'center' },
    { label: 'Min', valore: (r) => Number(r.qta_min), larghezza: 10, align: 'center' },
    { label: 'Stato', valore: (r) => configScorta(r.stato_scorta).label, larghezza: 16, align: 'center' },
    {
      label: 'Ubicazione',
      valore: (r) => (r.ubicazione_codice ? `${r.magazzino_label ?? ''} · ${r.ubicazione_codice}` : ''),
      larghezza: 22, align: 'center',
    },
    { label: 'Codice SAP', valore: (r) => r.codice_sap ?? '', larghezza: 16, align: 'left' },
    { label: 'Cod. fornitore', valore: (r) => r.codice_fornitore ?? '', larghezza: 18, align: 'left' },
  ];

  const descrizioneFiltri = () => {
    const parti = [];
    if (lista.ricercaInput.trim()) parti.push(`Ricerca: "${lista.ricercaInput.trim()}"`);
    filtriAttivi.forEach((f) => {
      const def = definizioniFiltri.find((d) => d.key === f.col);
      const valore = def?.tipo === 'select' ? (def.opzioni.find((o) => o.value === f.val)?.label ?? f.val) : f.val;
      parti.push(`${def?.label ?? f.col}: ${valore}`);
    });
    if (lista.mostraDisattivati) parti.push('Inclusi articoli disattivati');
    return parti.length ? `Filtri — ${parti.join(' · ')}` : '';
  };

  const esporta = async (formato) => {
    setEsportando(true);
    try {
      const { righe, totale, troncato } = await colataEsporta('articoli', {
        sort: lista.sort,
        ricerca: lista.ricercaInput,
        soloAttivi: !lista.mostraDisattivati,
        filtri,
      });
      if (righe.length === 0) {
        toast.warn('Nessun articolo da esportare.');
        return;
      }
      const base = { nomeFile: 'stock_colata', colonne: COLONNE_EXPORT, righe };
      if (formato === 'csv') esportaCSV(base);
      else if (formato === 'xlsx') await esportaXLSX({ ...base, nomeFoglio: 'Stock Colata' });
      else await esportaPDF({ ...base, nomeFile: 'Stock_Magazzino_Colata', titolo: 'SITUAZIONE STOCK MAGAZZINO COLATA', sottotitolo: descrizioneFiltri() });
      toast.success(`Esportati ${righe.length} articoli.`);
      if (troncato) toast.warn(`Esportazione limitata ai primi ${righe.length} su ${totale} articoli: restringi i filtri.`);
    } catch (e) {
      toast.error(mapColataError(e).message);
    } finally {
      setEsportando(false);
    }
  };

  // ─── Colonne tabella ───────────────────────────────────────────────────────
  const tutteLeColonne = [
    {
      key: 'codice', label: 'Codice', width: 150,
      render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground/60 group-hover:text-primary/70 transition-colors">{r.codice}</span>,
    },
    {
      key: 'nome', label: 'Articolo', width: 280,
      render: (r) => (
        <div className="flex flex-col gap-1">
          <span className="text-[13px] font-black uppercase tracking-tight text-foreground/75 group-hover:text-foreground transition-colors leading-tight">{r.nome}</span>
          {!r.attivo && <div><StatusPill tono="neutro" label="Disattivato" /></div>}
        </div>
      ),
    },
    {
      key: 'reparto_label', label: 'Ricambio', width: 130,
      render: (r) => <span className="text-[11px] font-black uppercase tracking-widest text-foreground/60">{r.reparto_label}</span>,
    },
    {
      key: 'zone_codici', label: 'Zona', width: 160, align: 'center',
      render: (r) => (r.zone_codici
        ? <span className="text-[11px] font-mono font-bold text-primary/80">{r.zone_codici}</span>
        : <span className="text-[11px] font-bold text-muted-foreground/30">—</span>),
    },
    {
      key: 'impianti_nomi', label: 'Impianto', width: 200,
      render: (r) => (r.impianti_nomi
        ? <span className="text-[11px] font-black uppercase tracking-wide text-foreground/60">{r.impianti_nomi}</span>
        : <span className="text-[11px] font-bold text-muted-foreground/30">—</span>),
    },
    {
      key: 'qta', label: 'Q.tà', width: 130, align: 'center',
      render: (r) => {
        const cfg = configScorta(r.stato_scorta);
        return (
          <div className="flex items-center justify-center gap-1.5">
            <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.punto}`} />
            <span className={`text-[15px] tabular-nums font-black ${cfg.colore}`}>{formattaQta(r.qta, r.unita_decimali)}</span>
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">{r.unita_label}</span>
          </div>
        );
      },
    },
    {
      key: 'qta_min', label: 'Min', width: 90, align: 'center',
      render: (r) => <span className="text-[12px] font-bold text-muted-foreground/40 tabular-nums">{formattaQta(r.qta_min, r.unita_decimali)}</span>,
    },
    {
      key: 'stato_scorta', label: 'Stato', width: 150, align: 'center',
      render: (r) => {
        const cfg = configScorta(r.stato_scorta);
        return <StatusPill tono={cfg.tono} label={cfg.label} pulse={r.stato_scorta === 'ok'} />;
      },
    },
    {
      key: 'ubicazione_codice', label: 'Ubicazione', width: 150,
      render: (r) => (
        <span className="text-[12px] font-bold text-muted-foreground">
          {r.ubicazione_codice ? `${r.magazzino_label ?? ''} · ${r.ubicazione_codice}` : '---'}
        </span>
      ),
    },
    {
      key: 'n_ordini_aperti', label: 'In ordine', width: 110, align: 'center',
      render: (r) => (r.n_ordini_aperti > 0
        ? <StatusPill tono="warn" label={`${formattaQta(r.qta_ordinata, r.unita_decimali)} ${r.unita_label}`} />
        : <span className="text-[11px] font-bold text-muted-foreground/30">—</span>),
    },
    {
      key: '__apri', label: '', width: 235, align: 'center', noSort: true,
      render: (r) => (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setApertoId(r.id); }}
            className="inline-flex items-center justify-center gap-1.5 w-10 h-10 md:w-auto md:px-5 md:py-2 rounded-2xl bg-primary/5 border border-primary/20 text-primary/60 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-105 active:scale-95 transition-all duration-300 text-[10px] font-black uppercase tracking-[.15em]"
          >
            <Icon name="Eye" size={15} strokeWidth={3} />
            <span className="hidden md:inline">Apri</span>
          </button>
          <button
            type="button"
            title="Compatibilità con i componenti"
            aria-label="Compatibilità con i componenti"
            onClick={(e) => { e.stopPropagation(); setCompatibilitaArticolo(r); }}
            className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-muted/30 border border-border/40 text-muted-foreground/60 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-105 active:scale-95 transition-all duration-300"
          >
            <Icon name="Link2" size={15} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            title="Cartellino / Codice a barre"
            aria-label="Cartellino / Codice a barre"
            onClick={(e) => { e.stopPropagation(); setCartellinoArticolo(r); }}
            className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-muted/30 border border-border/40 text-muted-foreground/60 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-105 active:scale-95 transition-all duration-300"
          >
            <Icon name="Barcode" size={15} strokeWidth={2.5} />
          </button>
        </div>
      ),
    },
  ];

  const colonne = tutteLeColonne.filter((c) => c.key === '__apri' || colVisibili[c.key] !== false);

  const rimuoviFiltro = (indice) => setFiltriUi((prec) => prec.filter((_, i) => i !== indice));

  return (
    <div className="w-full animate-in fade-in duration-700">
      {/* Intestazione */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6 px-1">
        <div>
          <h2 className="text-4xl md:text-5xl font-black tracking-[-0.06em] uppercase leading-none text-foreground">
            STOCK<span className="text-primary">COLATA</span>
          </h2>
          <div className="flex items-center gap-2 mt-2 md:mt-3">
            <span className="h-[2px] w-6 bg-primary rounded-full" />
            <p className="text-[10px] font-black uppercase tracking-[.35em] text-muted-foreground/50">
              Sistema di Gestione Magazzino
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setAuditAperto(true)}
            className={`relative flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm ${
              auditN > 0
                ? 'bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20'
                : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={auditN > 0 ? 'ShieldAlert' : 'ShieldCheck'} size={14} />
            <span className="hidden sm:inline">Controllo</span>
            {auditN > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center">
                {auditN}
              </span>
            )}
          </button>

          {scrivere && (
            <button
              type="button"
              onClick={() => setFormAperto(true)}
              className="flex items-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 group active:scale-95"
            >
              <Icon name="Plus" size={18} className="group-hover:rotate-90 transition-transform duration-300" />
              <span className="text-[10px] font-black uppercase tracking-[.2em]">Nuovo articolo</span>
            </button>
          )}
        </div>
      </div>

      {/* Toolbar: ricerca, esporta, impostazioni, disattivati, aggiorna */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
        <SearchInput value={lista.ricercaInput} onChange={lista.setRicercaInput} placeholder="Cerca articolo, codice, SAP..." onScansiona={lista.setRicercaInput} formatiScanner={FORMATI_SCANNER} />

        <ExportMenu onEsporta={esporta} inCorso={esportando} disabilitato={lista.loading} />

        <button
          type="button"
          onClick={() => setImpostazioniAperte(true)}
          className={`relative flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm ${
            filtriAttivi.length > 0
              ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
              : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name={filtriAttivi.length > 0 ? 'Filter' : 'Settings2'} size={14} />
          <span className="hidden sm:inline">{filtriAttivi.length > 0 ? `Filtri (${filtriAttivi.length})` : 'Impostazioni'}</span>
          {filtriAttivi.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-black flex items-center justify-center">
              {filtriAttivi.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => lista.setMostraDisattivati(!lista.mostraDisattivati)}
          className={`flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm ${
            lista.mostraDisattivati
              ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
              : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name={lista.mostraDisattivati ? 'Eye' : 'EyeOff'} size={14} />
          <span className="hidden sm:inline">Disattivati</span>
        </button>

        <button
          type="button"
          onClick={lista.ricarica}
          disabled={lista.loading}
          className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-primary transition-all duration-200 shadow-sm disabled:opacity-50"
        >
          <Icon name="RefreshCw" size={14} className={lista.loading ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">Aggiorna</span>
        </button>
      </div>

      {filtriAttivi.length > 0 && (
        <div className="mb-3">
          <FiltriBadge filtri={filtriUi} definizioni={definizioniFiltri} onRimuovi={rimuoviFiltro} onAzzera={() => setFiltriUi([])} />
        </div>
      )}

      <DataTable
        colonne={colonne}
        righe={lista.righe}
        totale={lista.totale}
        loading={lista.loading}
        pagina={lista.pagina}
        perPagina={lista.perPagina}
        onPagina={lista.setPagina}
        sort={lista.sort}
        onSort={lista.onSort}
        densita={densita}
        onRigaClick={(r) => setApertoId(r.id)}
        rigaDisattivata={(r) => r.attivo === false}
        messaggioVuoto={lista.ricercaInput || filtriAttivi.length ? 'Nessun articolo corrisponde alla ricerca' : 'Nessun articolo in magazzino'}
        altezzaMax="max-h-[65dvh]"
      />

      {/* Modal */}
      {impostazioniAperte && (
        <ImpostazioniTabella
          colonne={COLONNE_VISIBILI}
          colVisibili={colVisibili}
          setColVisibili={setColVisibili}
          densita={densita}
          setDensita={setDensita}
          filtri={filtriUi}
          setFiltri={setFiltriUi}
          definizioniFiltri={definizioniFiltri}
          maxFiltri={MAX_FILTRI}
          onClose={() => setImpostazioniAperte(false)}
        />
      )}

      {formAperto && (
        <FormArticolo
          record={null}
          onClose={() => setFormAperto(false)}
          onSaved={articoloCreato}
        />
      )}

      {apertoId && (
        <DettaglioArticolo
          articoloId={apertoId}
          onClose={() => setApertoId(null)}
          onAggiornato={dopoModifica}
        />
      )}

      {caricoIniziale && (
        <CaricoModal
          articolo={caricoIniziale}
          iniziale
          onClose={() => setCaricoIniziale(null)}
          onDone={() => {
            setCaricoIniziale(null);
            dopoModifica();
          }}
        />
      )}

      {compatibilitaArticolo && (
        <CompatibilitaModal
          articolo={compatibilitaArticolo}
          onClose={() => setCompatibilitaArticolo(null)}
          onCambiato={dopoModifica}
        />
      )}

      {cartellinoArticolo && (
        <CartellinoModal
          articolo={cartellinoArticolo}
          onClose={() => setCartellinoArticolo(null)}
        />
      )}

      {auditAperto && <AuditQta onClose={() => setAuditAperto(false)} />}
    </div>
  );
};

export default TabellaArticoli;
